/**
 * ╔══════════════════════════════════════════════════════════════╗
 * ║  siteConfig.ts — Site Starter Kit Configuration             ║
 * ║                                                              ║
 * ║  This is the ONLY file you need to edit when setting up      ║
 * ║  a new website. All business details, navigation, contact    ║
 * ║  info, and layout preferences are centralized here.          ║
 * ║                                                              ║
 * ║  HOW TO SET UP A NEW SITE:                                   ║
 * ║  1. Clone the project                                        ║
 * ║  2. Edit this file with the new client's details             ║
 * ║  3. Update page content in /src/app/pages/                   ║
 * ║  4. Done — everything else works out of the box              ║
 * ║                                                              ║
 * ║  The page builder, inline editing, Supabase persistence,     ║
 * ║  drag & drop, and all shared components work automatically.  ║
 * ╚══════════════════════════════════════════════════════════════╝
 */

/* ─────────────────────────────────────────────
   0. SITE ID — DATA ISOLATION
   ─────────────────────────────────────────────
   CRITICAL: Each cloned site MUST have a unique siteId!
   All content, comments, and snapshots are stored in a
   shared Supabase database. The siteId prefix ensures
   each site's data is isolated from the others.

   When cloning this project for a new client, change
   this value FIRST — before editing any content.

   Use a short, URL-safe identifier:
   - "client-name"   (e.g. "acme-plumbing")
   - "brand-city"    (e.g. "dental-tlv")
   ───────────────────────────────────────────── */

export const siteId = "default";

/* ─────────────────────────────────────────────
   1. BUSINESS IDENTITY
   ───────────────────────────────────────────── */

export const identity = {
  /** Company name in English / brand name */
  name: "BRAND",
  /** Company name in Hebrew */
  nameHebrew: "\u05E9\u05DD \u05D4\u05E2\u05E1\u05E7",
  /** Short tagline */
  tagline: "\u05EA\u05D9\u05D0\u05D5\u05E8 \u05E7\u05E6\u05E8 \u05E9\u05DC \u05D4\u05E2\u05E1\u05E7",
  /** Description for the footer */
  description:
    "\u05DB\u05D0\u05DF \u05D9\u05D5\u05E4\u05D9\u05E2 \u05EA\u05D9\u05D0\u05D5\u05E8 \u05E7\u05E6\u05E8 \u05E9\u05DC \u05D4\u05E2\u05E1\u05E7 \u05E9\u05DC\u05DB\u05DD \u2014 \u05DE\u05D4 \u05D0\u05EA\u05DD \u05E2\u05D5\u05E9\u05D9\u05DD, \u05DE\u05D4 \u05DE\u05D9\u05D9\u05D7\u05D3 \u05D0\u05EA\u05DB\u05DD, \u05D5\u05DC\u05DE\u05D4 \u05DB\u05D3\u05D0\u05D9 \u05DC\u05E4\u05E0\u05D5\u05EA \u05D0\u05DC\u05D9\u05DB\u05DD. \u05E9\u05D5\u05E8\u05D4 \u05E2\u05D3 \u05E9\u05DC\u05D5\u05E9 \u05E9\u05D5\u05E8\u05D5\u05EA.",
  /** Copyright line (auto-generated year) */
  copyright: `\u00A9 ${new Date().getFullYear()} BRAND - \u05E9\u05DD \u05D4\u05E2\u05E1\u05E7`,
  /** Rights text */
  rightsReserved: "\u05DB\u05DC \u05D4\u05D6\u05DB\u05D5\u05D9\u05D5\u05EA \u05E9\u05DE\u05D5\u05E8\u05D5\u05EA",
};

/* ─────────────────────────────────────────────
   2. CONTACT DETAILS
   ───────────────────────────────────────────── */

export const contact = {
  phone: "03-000-0000",
  phoneHref: "tel:030000000",
  mobile: "050-000-0000",
  whatsappUrl: "https://wa.me/972500000000",
  email: "info@example.co.il",
  emailHref: "mailto:info@example.co.il",
  address: "\u05E8\u05D7\u05D5\u05D1 \u05D4\u05D3\u05D5\u05D2\u05DE\u05D4 1, \u05EA\u05DC \u05D0\u05D1\u05D9\u05D1",
  addressNote: "\u05D4\u05D2\u05E2\u05D4 \u05D1\u05EA\u05D9\u05D0\u05D5\u05DD \u05DE\u05E8\u05D0\u05E9",
};

/* ─────────────────────────────────────────────
   3. BUSINESS HOURS
   ───────────────────────────────────────────── */

export const hours = [
  { days: "\u05D0'-\u05D4'", time: "09:00-18:00" },
  { days: "\u05D5'", time: "09:00-13:00" },
];

/* ─────────────────────────────────────────────
   4. NAVIGATION
   ─────────────────────────────────────────────
   Main navigation for Header, Footer, and
   Breadcrumbs. `children` creates dropdowns.
   ───────────────────────────────────────────── */

export interface NavItem {
  label: string;
  path: string;
  children?: { label: string; path: string }[];
}

export const navigation: NavItem[] = [
  { label: "\u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA", path: "/" },
  { label: "\u05D0\u05D5\u05D3\u05D5\u05EA", path: "/about" },
  {
    label: "\u05E9\u05D9\u05E8\u05D5\u05EA\u05D9\u05DD",
    path: "/services",
    children: [
      { label: "\u05E9\u05D9\u05E8\u05D5\u05EA \u05D0\u05F3", path: "/service-a" },
      { label: "\u05E9\u05D9\u05E8\u05D5\u05EA \u05D1\u05F3", path: "/service-b" },
    ],
  },
  {
    label: "\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD",
    path: "/products",
    children: [
      { label: "\u05DE\u05D5\u05E6\u05E8 \u05D0\u05F3", path: "/product-a" },
      { label: "\u05DE\u05D5\u05E6\u05E8 \u05D1\u05F3", path: "/product-b" },
    ],
  },
  { label: "\u05EA\u05D9\u05E7 \u05E2\u05D1\u05D5\u05D3\u05D5\u05EA", path: "/projects" },
  { label: "\u05D9\u05D9\u05E2\u05D5\u05E5", path: "/consulting" },
  { label: "\u05D1\u05DC\u05D5\u05D2", path: "/blog" },
  { label: "\u05E6\u05D5\u05E8 \u05E7\u05E9\u05E8", path: "/contact" },
];

/* ─────────────────────────────────────────────
   5. FOOTER LINKS
   ───────────────────────────────────────────── */

export const footerQuickLinks = [
  { label: "\u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA", path: "/" },
  { label: "\u05D0\u05D5\u05D3\u05D5\u05EA", path: "/about" },
  { label: "\u05E9\u05D9\u05E8\u05D5\u05EA\u05D9\u05DD", path: "/services" },
  { label: "\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD", path: "/products" },
  { label: "\u05EA\u05D9\u05E7 \u05E2\u05D1\u05D5\u05D3\u05D5\u05EA", path: "/projects" },
  { label: "\u05D9\u05D9\u05E2\u05D5\u05E5", path: "/consulting" },
  { label: "\u05D1\u05DC\u05D5\u05D2", path: "/blog" },
  { label: "\u05E6\u05D5\u05E8 \u05E7\u05E9\u05E8", path: "/contact" },
];

export const footerSolutionLinks = [
  { label: "\u05E9\u05D9\u05E8\u05D5\u05EA \u05D0\u05F3", path: "/service-a" },
  { label: "\u05E9\u05D9\u05E8\u05D5\u05EA \u05D1\u05F3", path: "/service-b" },
  { label: "\u05DE\u05D5\u05E6\u05E8 \u05D0\u05F3", path: "/product-a" },
  { label: "\u05DE\u05D5\u05E6\u05E8 \u05D1\u05F3", path: "/product-b" },
  { label: "\u05D9\u05D9\u05E2\u05D5\u05E5", path: "/consulting" },
];

/* ─────────────────────────────────────────────
   6. BREADCRUMBS
   ───────────────────────────────────────────── */

export const breadcrumbs: Record<string, { label: string; path?: string }[]> = {
  "/about": [{ label: "\u05D0\u05D5\u05D3\u05D5\u05EA" }],
  "/services": [{ label: "\u05E9\u05D9\u05E8\u05D5\u05EA\u05D9\u05DD" }],
  "/service-a": [
    { label: "\u05E9\u05D9\u05E8\u05D5\u05EA\u05D9\u05DD", path: "/services" },
    { label: "\u05E9\u05D9\u05E8\u05D5\u05EA \u05D0\u05F3" },
  ],
  "/service-b": [
    { label: "\u05E9\u05D9\u05E8\u05D5\u05EA\u05D9\u05DD", path: "/services" },
    { label: "\u05E9\u05D9\u05E8\u05D5\u05EA \u05D1\u05F3" },
  ],
  "/products": [{ label: "\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD" }],
  "/product-a": [
    { label: "\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD", path: "/products" },
    { label: "\u05DE\u05D5\u05E6\u05E8 \u05D0\u05F3" },
  ],
  "/product-b": [
    { label: "\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD", path: "/products" },
    { label: "\u05DE\u05D5\u05E6\u05E8 \u05D1\u05F3" },
  ],
  "/projects": [{ label: "\u05EA\u05D9\u05E7 \u05E2\u05D1\u05D5\u05D3\u05D5\u05EA" }],
  "/consulting": [{ label: "\u05D9\u05D9\u05E2\u05D5\u05E5" }],
  "/blog": [{ label: "\u05D1\u05DC\u05D5\u05D2" }],
  "/contact": [{ label: "\u05E6\u05D5\u05E8 \u05E7\u05E9\u05E8" }],
  "/seo-manager": [{ label: "\u05E0\u05D9\u05D4\u05D5\u05DC SEO" }],
  "/change-history": [{ label: "\u05D4\u05D9\u05E1\u05D8\u05D5\u05E8\u05D9\u05D9\u05EA \u05E9\u05D9\u05E0\u05D5\u05D9\u05D9\u05DD" }],
  "/business-info": [{ label: "\u05E8\u05E7\u05E2 \u05E2\u05DC \u05D4\u05E2\u05E1\u05E7" }],
  "/qa-checklist": [{ label: "\u05E6\u05F3\u05E7\u05DC\u05D9\u05E1\u05D8 QA" }],
  "/site-tree": [{ label: "\u05E8\u05E7\u05E2 \u05E2\u05DC \u05D4\u05E2\u05E1\u05E7" }],
};

/* ─────────────────────────────────────────────
   7. LAYOUT PREFERENCES
   ───────────────────────────────────────────── */

export const layout = {
  /** Pages that should NOT show the bottom contact form */
  hideContactFormOn: ["/contact", "/seo-manager", "/change-history", "/business-info", "/qa-checklist", "/site-tree"],
  /** Home page path */
  homePath: "/",
  /** Font family */
  fontFamily: "var(--font-family)",
  /** Home breadcrumb label */
  homeBreadcrumbLabel: "\u05D3\u05E3 \u05D4\u05D1\u05D9\u05EA",
};

/* ─────────────────────────────────────────────
   8. CONTACT FORM DEFAULTS
   ───────────────────────────────────────────── */

export const contactForm = {
  title: "\u05E6\u05E8\u05D5 \u05E7\u05E9\u05E8",
  subtitle: "\u05DE\u05E2\u05D5\u05E0\u05D9\u05D9\u05E0\u05D9\u05DD \u05DC\u05E9\u05DE\u05D5\u05E2 \u05E2\u05D5\u05D3? \u05D4\u05E9\u05D0\u05D9\u05E8\u05D5 \u05E4\u05E8\u05D8\u05D9\u05DD \u05D5\u05E0\u05D7\u05D6\u05D5\u05E8 \u05D0\u05DC\u05D9\u05DB\u05DD \u05D1\u05D4\u05E7\u05D3\u05DD.",
  submitLabel: "\u05E9\u05DC\u05D9\u05D7\u05D4",
  fields: {
    namePlaceholder: "\u05E9\u05DD \u05DE\u05DC\u05D0",
    phonePlaceholder: "\u05D8\u05DC\u05E4\u05D5\u05DF",
    emailPlaceholder: "\u05DB\u05EA\u05D5\u05D1\u05EA \u05DE\u05D9\u05D9\u05DC",
    messagePlaceholder: "\u05E1\u05E4\u05E8\u05D5 \u05DC\u05E0\u05D5 \u05D1\u05DE\u05D4 \u05E0\u05D5\u05DB\u05DC \u05DC\u05E2\u05D6\u05D5\u05E8...",
  },
};

/* ─────────────────────────────────────────────
   9. CTA DEFAULTS
   ─────────────────────────────────────────────
   Default button labels/links for CTA sections
   across all pages.
   ───────────────────────────────────────────── */

export const ctaDefaults = {
  primaryLabel: "\u05E7\u05D1\u05DC\u05D5 \u05D9\u05D9\u05E2\u05D5\u05E5 \u05DC\u05DC\u05D0 \u05E2\u05DC\u05D5\u05EA",
  primaryLink: "/contact",
  phoneLabel: `\u05D4\u05EA\u05E7\u05E9\u05E8\u05D5: ${contact.phone}`,
  phoneHref: contact.phoneHref,
  whatsappLabel: "\u05D5\u05D5\u05D0\u05D8\u05E1\u05D0\u05E4",
  whatsappHref: contact.whatsappUrl,
};

/* ─────────────────────────────────────────────
   10. CONTACT PAGE — INTEREST AREAS
   ───────────────────────────────────────────── */

export const contactInterestAreas = [
  "\u05E9\u05D9\u05E8\u05D5\u05EA \u05D0\u05F3",
  "\u05E9\u05D9\u05E8\u05D5\u05EA \u05D1\u05F3",
  "\u05DE\u05D5\u05E6\u05E8 \u05D0\u05F3",
  "\u05DE\u05D5\u05E6\u05E8 \u05D1\u05F3",
  "\u05D9\u05D9\u05E2\u05D5\u05E5",
  "\u05E9\u05D9\u05EA\u05D5\u05E3 \u05E4\u05E2\u05D5\u05DC\u05D4",
  "\u05D0\u05D7\u05E8",
];